import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import dayjs from "dayjs";
import { CategoryMetadata, MetricToValueMap, MetricToMetadataMap } from "../../lossAnalysis/models";
import {
  getLossAnalysisAsync,
  getEarliestTimestampAsync,
  getLatestTimestampAsync,
  getLossAnalysisCategoriesMetadataAsync,
  getMetricsMetadataAsync,
} from "../../lossAnalysis/LossAnalysisApi";
import { LoadingState } from "../LoadingState";
import { getTagsHistoryLatestAsync } from "../TagsAPI";
import { IHistoryTagsParams } from "../../data_point/models/TagChartModel";
import { Tags } from "../../data_point/tags/TagCatalog";
import { validateResponse } from "../../data_point/TagValidationUtils";
import { RootState } from "../Store";
import { createScalarTagSelection } from "../TagQueryUtils";
import { beginRequest, completeRequest, rejectRequest, RequestState } from "./RequestState";

interface ResourceState<T> extends RequestState {
  data?: T;
}
interface LossAnalysisStoreState {
  data: Record<
    string,
    Record<string, { metrics: ResourceState<MetricToValueMap>; actualProduction: ResourceState<number> }>
  >;
  metadata: {
    categoriesMetadata: ResourceState<CategoryMetadata[]>;
    metricsMetadata: ResourceState<MetricToMetadataMap>;
  };
  earliestTimestamps: Record<string, ResourceState<number | null>>;
  latestTimestamps: Record<string, ResourceState<number | null>>;
}
const initialState: LossAnalysisStoreState = {
  data: {},
  metadata: {
    categoriesMetadata: { loadingState: LoadingState.None },
    metricsMetadata: { loadingState: LoadingState.None },
  },
  earliestTimestamps: {},
  latestTimestamps: {},
};
export const getMetricsTimeRangeKey = (start: Date | number, end: Date | number): string =>
  `${new Date(start).toISOString()}-${new Date(end).toISOString()}`;
const initializeSiteData = (state: LossAnalysisStoreState, siteId: string, key: string) => {
  state.data[siteId] ??= {};
  state.data[siteId][key] ??= {
    metrics: { loadingState: LoadingState.None },
    actualProduction: { loadingState: LoadingState.None },
  };
};
export const lossAnalysisSlice = createSlice({
  name: "lossAnalysis",
  initialState,
  reducers: {},
  extraReducers: (builder) =>
    builder
      .addCase(fetchLossAnalysis.pending, (state, action) => {
        const { siteId, start, end } = action.meta.arg;
        const key = getMetricsTimeRangeKey(start, end);
        initializeSiteData(state, siteId, key);
        beginRequest(state.data[siteId][key].metrics, action.meta.requestId);
      })
      .addCase(fetchLossAnalysis.fulfilled, (state, action) => {
        const { siteId, start, end } = action.meta.arg;
        const current = state.data[siteId]?.[getMetricsTimeRangeKey(start, end)]?.metrics;
        if (!current || !completeRequest(current, action.meta.requestId)) {
          return;
        }
        current.data = action.payload?.metrics.reduce<MetricToValueMap>((acc, metric) => {
          acc[metric.name] = metric.valueInKWh;
          return acc;
        }, {} as MetricToValueMap);
      })
      .addCase(fetchLossAnalysis.rejected, (state, action) => {
        const { siteId, start, end } = action.meta.arg;
        const current = state.data[siteId]?.[getMetricsTimeRangeKey(start, end)]?.metrics;
        if (current) {
          rejectRequest(current, action);
        }
      })
      .addCase(fetchActualProduction.pending, (state, action) => {
        const { siteId, start, end } = action.meta.arg;
        const key = getMetricsTimeRangeKey(start, end);
        initializeSiteData(state, siteId, key);
        beginRequest(state.data[siteId][key].actualProduction, action.meta.requestId);
      })
      .addCase(fetchActualProduction.fulfilled, (state, action) => {
        const { siteId, start, end } = action.meta.arg;
        const current = state.data[siteId]?.[getMetricsTimeRangeKey(start, end)]?.actualProduction;
        if (current && completeRequest(current, action.meta.requestId)) {
          current.data = action.payload;
        }
      })
      .addCase(fetchActualProduction.rejected, (state, action) => {
        const { siteId, start, end } = action.meta.arg;
        const current = state.data[siteId]?.[getMetricsTimeRangeKey(start, end)]?.actualProduction;
        if (current) {
          rejectRequest(current, action);
        }
      })
      .addCase(fetchEarliestTimestamp.pending, (state, action) => {
        const current = (state.earliestTimestamps[action.meta.arg] ??= {
          loadingState: LoadingState.None,
        });
        beginRequest(current, action.meta.requestId);
      })
      .addCase(fetchEarliestTimestamp.fulfilled, (state, action) => {
        const current = state.earliestTimestamps[action.meta.arg];
        if (current && completeRequest(current, action.meta.requestId)) {
          current.data = action.payload;
        }
      })
      .addCase(fetchEarliestTimestamp.rejected, (state, action) => {
        const current = state.earliestTimestamps[action.meta.arg];
        if (current) {
          rejectRequest(current, action);
        }
      })
      .addCase(fetchLatestTimestamp.pending, (state, action) => {
        const current = (state.latestTimestamps[action.meta.arg] ??= {
          loadingState: LoadingState.None,
        });
        beginRequest(current, action.meta.requestId);
      })
      .addCase(fetchLatestTimestamp.fulfilled, (state, action) => {
        const current = state.latestTimestamps[action.meta.arg];
        if (current && completeRequest(current, action.meta.requestId)) {
          current.data = action.payload;
        }
      })
      .addCase(fetchLatestTimestamp.rejected, (state, action) => {
        const current = state.latestTimestamps[action.meta.arg];
        if (current) {
          rejectRequest(current, action);
        }
      })
      .addCase(fetchCategoriesMetadata.pending, (state, action) => {
        beginRequest(state.metadata.categoriesMetadata, action.meta.requestId);
      })
      .addCase(fetchCategoriesMetadata.fulfilled, (state, action) => {
        const current = state.metadata.categoriesMetadata;
        if (completeRequest(current, action.meta.requestId)) {
          current.data = action.payload;
        }
      })
      .addCase(fetchCategoriesMetadata.rejected, (state, action) => {
        rejectRequest(state.metadata.categoriesMetadata, action);
      })
      .addCase(fetchMetricsMetadata.pending, (state, action) => {
        beginRequest(state.metadata.metricsMetadata, action.meta.requestId);
      })
      .addCase(fetchMetricsMetadata.fulfilled, (state, action) => {
        const current = state.metadata.metricsMetadata;
        if (completeRequest(current, action.meta.requestId)) {
          current.data = action.payload.reduce<MetricToMetadataMap>((acc, metric) => {
            acc[metric.name] = metric;
            return acc;
          }, {} as MetricToMetadataMap);
        }
      })
      .addCase(fetchMetricsMetadata.rejected, (state, action) => {
        rejectRequest(state.metadata.metricsMetadata, action);
      }),
});

export const fetchLossAnalysis = createAsyncThunk(
  "lossAnalysis/fetchLossAnalysis",
  async ({ siteId, start, end }: { siteId: string; start: number; end: number }, { signal }) => {
    return await getLossAnalysisAsync(siteId, new Date(start), new Date(end), signal);
  }
);

export const fetchEarliestTimestamp = createAsyncThunk(
  "lossAnalysis/fetchEarliestTimestamp",
  async (siteId: string, { signal }) => {
    return (await getEarliestTimestampAsync(siteId, signal))?.getTime() ?? null;
  }
);

export const fetchLatestTimestamp = createAsyncThunk(
  "lossAnalysis/fetchLatestTimestamp",
  async (siteId: string, { signal }) => {
    return (await getLatestTimestampAsync(siteId, signal))?.getTime() ?? null;
  }
);

export const fetchCategoriesMetadata = createAsyncThunk(
  "lossAnalysis/fetchCategoriesMetadata",
  async () => {
    return await getLossAnalysisCategoriesMetadataAsync();
  }
);

export const fetchMetricsMetadata = createAsyncThunk("lossAnalysis/fetchMetricsMetadata", async () => {
  return await getMetricsMetadataAsync();
});

export const fetchActualProduction = createAsyncThunk(
  "lossAnalysis/fetchActualProduction",
  async ({
    siteId,
    start,
    end,
    endOverride,
  }: {
    siteId: string;
    start: number;
    end: number;
    endOverride?: number;
  }) => {
    const samplingInterval = 86400;
    const params: IHistoryTagsParams = {
      siteId: siteId,
      selections: [createScalarTagSelection(Tags.Portal.DailyProduction.AcPower.logicalName)],
      range: {
        start: dayjs(start),
        end: dayjs(endOverride ?? end),
      },
      samplingIntervalInSeconds: samplingInterval,
    };
    const apiResponse = await getTagsHistoryLatestAsync(params);
    validateResponse(apiResponse, params, samplingInterval);

    const tagData = apiResponse[Tags.Portal.DailyProduction.AcPower.logicalName];
    if (tagData.length === 0) {
      return undefined;
    }
    const totalProduction = tagData.reduce((sum, dataPoint) => sum + dataPoint.value, 0);
    return totalProduction;
  }
);

export const selectMetadataAggregatedLoadingState = (state: RootState) => {
  const categoriesState = state.lossAnalysis.metadata.categoriesMetadata.loadingState;
  const metricsState = state.lossAnalysis.metadata.metricsMetadata.loadingState;

  if (categoriesState === LoadingState.Error || metricsState === LoadingState.Error) {
    return LoadingState.Error;
  }
  if (categoriesState === LoadingState.Pending || metricsState === LoadingState.Pending) {
    return LoadingState.Pending;
  }
  if (categoriesState === LoadingState.Complete && metricsState === LoadingState.Complete) {
    return LoadingState.Complete;
  }
  return LoadingState.None;
};

export const lossAnalysisReducer = lossAnalysisSlice.reducer;
const timestampViews = new WeakMap<ResourceState<number | null>, ResourceState<Date | null>>();
function timestampView(
  entry: ResourceState<number | null> | undefined
): ResourceState<Date | null> | undefined {
  if (!entry) {
    return undefined;
  }
  let view = timestampViews.get(entry);
  if (!view) {
    view = { ...entry, data: entry.data == null ? entry.data : new Date(entry.data) };
    timestampViews.set(entry, view);
  }
  return view;
}
export const selectEarliestTimestampView = (state: RootState, siteId: string) =>
  timestampView(state.lossAnalysis.earliestTimestamps[siteId]);
export const selectLatestTimestampView = (state: RootState, siteId: string) =>
  timestampView(state.lossAnalysis.latestTimestamps[siteId]);
